import { DataSnapshot } from 'firebase/database';

import { Encryption } from '@core';
import { DataBaseNodes } from '@lib';
import { useSiteStore } from '@stores';
import {
  ChatMessage,
  Site,
  SourceUnsubscribe,
  TypingStatusObject,
  UnReadMessages,
} from '@type';
import { getDateUTCOffset } from '@utils';

import { ChatNodeValue } from '../types';
import { BaseGateway } from './base.gateway';

export class ChatGateway extends BaseGateway {
  requestChatHistory = async (siteUuid: string) => {
    const payload: { [key: string]: unknown } = {
      preference: DataBaseNodes.ChatHistory,
      type: DataBaseNodes.ChatHistory,
      utcOffset: getDateUTCOffset(),
    };
    return await this.sendRequest(
      siteUuid,
      payload,
      DataBaseNodes.Request,
      this.config.patientSourceUUID
    );
  };

  subscribeToChatHistory = (
    callback: (message: ChatMessage[] | null, site: Site) => void
  ): SourceUnsubscribe => {
    return this.sourceService.subscribeToChild(
      `${DataBaseNodes.Patients}/${this.config.patientSourceUUID}/${DataBaseNodes.ChatHistory}`,
      (snapshot: DataSnapshot) => {
        if (!snapshot.exists()) return [];
        const value = Object.values(
          snapshot.val() as {
            [key: string]: ChatNodeValue;
          }
        );
        for (const { document, siteUUID } of value) {
          const siteInfo = useSiteStore.getState().getSiteByUUID(siteUUID);
          const fetchedMessages = Encryption.decryptAndParseObject<
            ChatMessage[]
          >(document, siteInfo.sharedSecretKey);
          callback(fetchedMessages, siteInfo);
        }
      }
    );
  };

  uploadFile = async (file: File) => {
    return this.sourceService.uploadFile(
      file,
      `${DataBaseNodes.Patients}/${this.config.patientSourceUUID}/${DataBaseNodes.Files}/`
    );
  };

  removeChatHistoryNode = async () => {
    await this.sourceService
      .removeData(
        `${DataBaseNodes.Patients}/${this.config.patientSourceUUID}/${DataBaseNodes.ChatHistory}/`
      )
      .catch(error => {
        throw new Error(
          `Removing ChatHistory has been failed: ${JSON.stringify(error)}`
        );
      });
  };

  sendChatMessage = async (siteUuid: string, message: string) => {
    const { getSiteByUUID } = useSiteStore.getState();
    const siteData = getSiteByUUID(siteUuid);

    const payload = {
      isRead: false,
      message: message,
      hashedSharedKey: siteData.hashedSharedKey,
      type: DataBaseNodes.ChatMessage,
      deviceOffset: getDateUTCOffset(),
    };

    return await this.sendRequest(siteUuid, payload, DataBaseNodes.ChatMessage);
  };

  subscribeToUnReadChatMessage = (
    callback: (unReadMessages: UnReadMessages) => void
  ): SourceUnsubscribe => {
    return this.sourceService.subscribeToChild(
      `${DataBaseNodes.Patients}/${this.config.patientSourceUUID}/${DataBaseNodes.ChatMessage}`,
      (snapshot: DataSnapshot) => {
        const data = snapshot.exists()
          ? (snapshot.val() as UnReadMessages)
          : {};
        callback(data);
      }
    );
  };

  subscribeToChatMessage = (
    careteamUUID: string,
    callback: (
      message: ChatMessage | null,
      site: Site,
      key: string | null
    ) => void
  ): SourceUnsubscribe => {
    return this.sourceService.subscribeToChildAdded(
      `${DataBaseNodes.Patients}/${this.config.patientSourceUUID}/${DataBaseNodes.ChatMessage}`,
      (snapshot: DataSnapshot) => {
        if (!snapshot.exists()) return {};

        const key = snapshot.key;
        const value = snapshot.val() as ChatNodeValue;

        const { document, siteUUID } = value;
        if (careteamUUID === siteUUID) {
          const siteInfo = useSiteStore.getState().getSiteByUUID(siteUUID);
          const fetchedMessage = Encryption.decryptAndParseObject<ChatMessage>(
            document,
            siteInfo.sharedSecretKey
          );
          callback(fetchedMessage, siteInfo, key);
        }
      }
    );
  };

  removeChatMessageNode = async (key: string) => {
    await this.sourceService
      .removeData(
        `${DataBaseNodes.Patients}/${this.config.patientSourceUUID}/${DataBaseNodes.ChatMessage}/${key}`
      )
      .catch(error => {
        throw new Error(
          `Removing ChatMessage has been failed: ${JSON.stringify(error)}`
        );
      });
  };

  sendTypingStatus = async (status: TypingStatusObject) => {
    const payload: TypingStatusObject = {
      ...status,
    };

    return await this.sourceService.updateData<TypingStatusObject>(
      `${DataBaseNodes.Patients}/${this.config.patientSourceUUID}/${DataBaseNodes.TypingStatus}/${DataBaseNodes.Patient}`,
      payload
    );
  };

  removeTypingStatusNode = async () => {
    await this.sourceService
      .removeData(
        `${DataBaseNodes.Patients}/${this.config.patientSourceUUID}/${DataBaseNodes.TypingStatus}/${DataBaseNodes.Patient}`
      )
      .catch(error => {
        throw new Error(
          `Removing ChatMessage has been failed: ${JSON.stringify(error)}`
        );
      });
  };

  subscribeToTypingStatus = (
    callback: (typingStatus: TypingStatusObject) => void
  ): SourceUnsubscribe => {
    return this.sourceService.subscribeToChildAdded(
      `${DataBaseNodes.Patients}/${this.config.patientSourceUUID}/${DataBaseNodes.TypingStatus}/${DataBaseNodes.CareTeam}`,
      (snapshot: DataSnapshot) => {
        if (!snapshot.exists()) return null;
        if (snapshot.exists()) {
          const typingStatus = {
            ...snapshot.val(),
            key: snapshot.key,
          } as TypingStatusObject;
          callback(typingStatus);
        }
      }
    );
  };

  subscribeToTypingStatusRemoved = (
    callback: (typingStatus: TypingStatusObject) => void
  ): SourceUnsubscribe => {
    return this.sourceService.subscribeToChildRemoved(
      `${DataBaseNodes.Patients}/${this.config.patientSourceUUID}/${DataBaseNodes.TypingStatus}/${DataBaseNodes.CareTeam}`,
      (snapshot: DataSnapshot) => {
        if (!snapshot.exists()) return null;
        if (snapshot.exists()) {
          const typingStatus = {
            ...snapshot.val(),
            key: snapshot.key,
          } as TypingStatusObject;
          callback(typingStatus);
        }
      }
    );
  };
}
